import { makeStyles } from '@material-ui/core/styles';
import { useRecoilValue } from 'recoil';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import parse from 'react-html-parser';
import { localeState } from '../../../api/appState';
import { ProductDetailProduct } from '../../../api/products/productDetail';
import { SupportedLocale } from '../../../i18n/types';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';

export interface ProductDetailDescriptionProps extends StandardDivProps<ProductDetailDescriptionClassKey> {
  product: ProductDetailProduct;
}

export type ProductDetailDescriptionClassKey = 'root' | 'descriptionText' | 'showMoreBtn' | 'descriptionTextExpanded';

const themeSettings = { name: 'ProductDetailDescription' };
const useStyles = makeStyles(
  (theme) => ({
    root: {
      ...theme.typography.body1,
      color: theme.palette.text.primary,
    },
    descriptionText: {
      position: 'relative',
      overflow: 'hidden',
      maxHeight: 184,
    },
    descriptionTextExpanded: {
      position: 'relative',
      overflow: 'visible',
      height: 'auto',
    },
    showMoreBtn: {
      textAlign: 'left',
      width: '100%',
      backgroundColor: 'transparent',
      border: 'none',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      padding: '0 10px',
      fontSize: 14,
      fontWeight: 500,
      textDecoration: 'underline',
    },
  }),
  themeSettings,
);

const ProductDetailDescription: React.FC<ProductDetailDescriptionProps> = React.forwardRef(
  ({ className, classes: pClasses, product, ...rest }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const t = useTranslateWithFallback();
    const locale = useRecoilValue(localeState);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showMoreButton, setShowMoreButton] = useState(true);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const isContentOverflowing = descriptionRef.current ? descriptionRef.current.offsetHeight > 184 : false;

    useEffect(() => {
      setShowMoreButton(Boolean(isContentOverflowing));
    }, [product, locale, descriptionRef.current?.offsetHeight, isContentOverflowing]);

    const toggleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    return (
      <div ref={ref} className={clsx(classes.root, className)} {...rest}>
        <div className={clsx(isExpanded ? classes.descriptionTextExpanded : classes.descriptionText)}>
          <p ref={descriptionRef}>{getProductDescription(product, locale)}</p>
        </div>
        {showMoreButton && (
          <button onClick={toggleExpand} className={classes.showMoreBtn}>
            {isExpanded ? t('app/products/showLess') : t('app/products/showMore')}
          </button>
        )}

        {product.taxonomies?.includes('/pharmakon') && (
          <p>
            <b>{t('productDetailPage/limited')}</b>
          </p>
        )}
      </div>
    );
  },
);

function getProductDescription(product: ProductDetailProduct, locale: SupportedLocale): ReactElement[] | null {
  if (!product || !product.description || !product.description.length) return null;

  const description = product.description.find((d) => d.language === locale)?.text;
  if (description) return parse(description);
  if (locale === 'cs') return null;

  const csDescription = product.description.find((d) => d.language === 'cs')?.text;
  if (csDescription) return parse(csDescription);
  return null;
}

ProductDetailDescription.displayName = 'ProductDetailDescription';

export default withThemeProps(themeSettings)(ProductDetailDescription);
